import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Eye, CheckCircle, Ticket, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TICKET_TYPE_LABELS = {
  chamada_agendada: 'Novo Pedido de Chamada Agendada',
  chamada_imediata: 'Novo Pedido de Chamada Imediata',
  tratamento_pendencia: 'Tratamento Pendência',
  pedido_anulacao: 'Pedido Anulação',
};

export const TicketsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const canManage = ['super_admin', 'gestor', 'admin', 'backoffice', 'parceiro'].includes(user?.role);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tipo: '',
    description: '',
    priority: 'normal',
    contract_id: '',
  });

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterTipo, filterPriority]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterTipo !== 'all') params.tipo = filterTipo;
      if (filterPriority !== 'all') params.priority = filterPriority;
      const ticketsRes = await axios.get(`${API_URL}/tickets`, { params });
      setTickets(ticketsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  useRefreshListener(fetchData);

  const visibleTickets = showOnlyUnread ? tickets.filter(t => t.unread) : tickets;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipo) {
      toast.error('Tipo é obrigatório');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/tickets`, formData);
      toast.success('Ticket criado');
      setDialogOpen(false);
      setFormData({ tipo: '', description: '', priority: 'normal', contract_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar ticket');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API_URL}/tickets/${ticketId}`, { status: newStatus });
      toast.success('Estado atualizado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: pt });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="tickets-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Ticket className="w-6 h-6 text-[#2D3E6F]" />
              Tickets
            </h1>
            <p className="text-slate-500 mt-1">{tickets.length} tickets registados</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
                data-testid="create-ticket-button"
              >
                <Plus className="w-4 h-4" />
                Novo Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle className="font-outfit">Novo Ticket</DialogTitle>
                  <DialogDescription>
                    Crie um novo ticket de suporte
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="tipo">Tipo *</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="ticket-tipo-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chamada_agendada">Novo Pedido de Chamada Agendada</SelectItem>
                        <SelectItem value="chamada_imediata">Novo Pedido de Chamada Imediata</SelectItem>
                        <SelectItem value="tratamento_pendencia">Tratamento Pendência</SelectItem>
                        <SelectItem value="pedido_anulacao">Pedido Anulação</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descreva o problema..."
                      className="mt-1.5 min-h-[100px]"
                      data-testid="ticket-description-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Prioridade</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => setFormData({ ...formData, priority: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baixa">Baixa</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="urgente">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                    data-testid="save-ticket-button"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A criar...
                      </>
                    ) : (
                      'Criar Ticket'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters bar */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex flex-wrap items-center gap-3" data-testid="tickets-filters">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="novo">Novo</SelectItem>
                  <SelectItem value="em_tratamento">Em Tratamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Tipo</Label>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="h-9" data-testid="filter-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="chamada_agendada">Chamada Agendada</SelectItem>
                  <SelectItem value="chamada_imediata">Chamada Imediata</SelectItem>
                  <SelectItem value="tratamento_pendencia">Tratamento Pendência</SelectItem>
                  <SelectItem value="pedido_anulacao">Pedido Anulação</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Prioridade</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="h-9" data-testid="filter-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col items-start gap-1">
              <Label className="text-xs text-slate-500">Apenas não lidos</Label>
              <Button
                type="button"
                variant={showOnlyUnread ? 'default' : 'outline'}
                onClick={() => setShowOnlyUnread(v => !v)}
                className={`h-9 rounded-full ${showOnlyUnread ? 'bg-[#2D3E6F] hover:bg-[#1a2a4a]' : ''}`}
                data-testid="filter-unread-toggle"
              >
                {showOnlyUnread ? '✓ Não lidos' : 'Não lidos'}
              </Button>
            </div>
            {(filterStatus !== 'all' || filterTipo !== 'all' || filterPriority !== 'all' || showOnlyUnread) && (
              <Button
                variant="ghost"
                onClick={() => { setFilterStatus('all'); setFilterTipo('all'); setFilterPriority('all'); setShowOnlyUnread(false); }}
                className="h-9 self-end"
                data-testid="filter-clear"
              >
                Limpar filtros
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : visibleTickets.length === 0 ? (
            <CardContent className="text-center py-16">
              <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400">Nenhum ticket encontrado</p>
              <Button
                onClick={() => setDialogOpen(true)}
                variant="outline"
                className="mt-4"
              >
                Criar primeiro ticket
              </Button>
            </CardContent>
          ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-2"></TableHead>
                  <TableHead className="font-semibold">Tipo</TableHead>
                  <TableHead className="font-semibold">Contrato</TableHead>
                  <TableHead className="font-semibold">Prioridade</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold">Atribuído</TableHead>
                  <TableHead className="font-semibold">Criado por</TableHead>
                  <TableHead className="font-semibold">Data</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTickets.map((ticket, index) => (
                  <TableRow
                    key={ticket.id}
                    className={`hover:bg-slate-50/50 cursor-pointer ${ticket.unread ? 'bg-blue-50/40' : ''}`}
                    data-testid={`ticket-row-${index}`}
                    onClick={() => navigate(`/tickets/${ticket.id}`, { state: { returnTo: location.pathname + location.search } })}
                  >
                    <TableCell className="p-0 pl-2">
                      {ticket.unread ? (
                        <span className="block w-2 h-2 rounded-full bg-blue-500" title="Não lido" data-testid={`unread-dot-${ticket.id}`} />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${ticket.unread ? 'text-slate-900 font-semibold' : 'text-[#2D3E6F]'}`}>
                          {TICKET_TYPE_LABELS[ticket.tipo] || ticket.tipo}
                        </span>
                        {ticket.comment_count > 0 && (
                          <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full" title="Respostas">
                            {ticket.comment_count}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {ticket.contract_name || '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.priority} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.status} />
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {ticket.assigned_to_name || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {ticket.created_by_name || '-'}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(ticket.created_at)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canManage ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {ticket.status !== 'concluido' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(ticket.id, 'concluido')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Marcar como concluído
                              </DropdownMenuItem>
                            )}
                            {ticket.status !== 'em_tratamento' && ticket.status !== 'concluido' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(ticket.id, 'em_tratamento')}>
                                Em Tratamento
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};
